// pages/UserManagementPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, clearError } from '../slices/usersSlice';
import { useToast } from '../context/ToastContext';
import UserListTab from '../components/userManagement/UserListTab';
import UserCreateTab from '../components/userManagement/UserCreateTab';
import UserBulkCreateTab from '../components/userManagement/UserBulkCreateTab';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

const UserManagementPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { users, loading, error } = useSelector((state) => state.users);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      showToast(error.detail || error.message || 'حدث خطأ', 'error');
      dispatch(clearError());
    }
  }, [error, showToast, dispatch]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    dispatch(fetchUsers());
  };

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PeopleAltIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                إدارة المستخدمين
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                إنشاء المستخدمين وإدارة الصلاحيات والحسابات
              </Typography>
            </Box>
          </Stack>
        </MotionPaper>

        {/* Tabs & Content Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid #E5E7EB',
              bgcolor: '#F1F5F9',
              px: 1,
              '& .MuiTab-root': {
                fontFamily: 'Cairo, Tajawal, sans-serif',
                fontWeight: 700,
                fontSize: '0.9rem',
                textTransform: 'none',
                color: '#6B7280',
                minHeight: 52,
              },
              '& .Mui-selected': {
                color: '#4338CA !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#4338CA',
                height: 3,
                borderRadius: 3,
              },
            }}
          >
            <Tab label="قائمة المستخدمين" />
            <Tab label="إنشاء مستخدم" />
            <Tab label="إنشاء مجمع للطلاب" />
          </Tabs>

          {loading && tabValue === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#4338CA' }} />
            </Box>
          )}

          {tabValue === 0 && !loading && (
            <UserListTab users={users} onRefresh={handleRefresh} />
          )}

          {tabValue === 1 && (
            <UserCreateTab onSuccess={handleRefresh} />
          )}

          {tabValue === 2 && (
            <UserBulkCreateTab onSuccess={handleRefresh} />
          )}
        </MotionPaper>
      </Box>
    </Box>
  );
};

export default UserManagementPage;
